import { Injectable, Inject } from '@angular/core';
import { Subject } from 'rxjs';

import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { GetDateStringProvider, GET_DATE_STRING } from '@finder/shared/utils/string/getDateString';
import { GetFullDateStringProvider, GET_FULL_DATE_STRING } from '@finder/shared/utils/string/getFullDateString';
import { LOCALES, SCHEDULE_TYPES } from '@finder/shared/constants/app.constants';
import { UserSelectionStorage } from '@finder/shared/services/user-selection-storage/user-selection-storage.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { MealPeriodScheduleService } from '../meal-period-schedule/meal-period-schedule.service';
import { SsrService } from '../ssr/ssr.service';
import { GET_SIMPLE_DATE_FROM_DATE_OBJECT, GetSimpleDateFromDateObjectProvider } from '@finder/shared/utils/date/getSimpleDateFromDateObject';

@Injectable()
export class ScheduleService {

    date: Date = new Date();
    fullFormatDate: string = this.getFullDateString(this.date);
    schedule: any;
    entityId: string;
    isLoading = false;
    hasMealPeriod = false;

    private subject = new Subject<any>();
    get scheduleUpdated() {
        return this.subject.asObservable();
    }

    constructor(
        private finderApi: FinderApiConnectorService,
        private storage: UserSelectionStorage,
        private localeService: LocaleService,
        private mealPeriodScheduleService: MealPeriodScheduleService,
        private ssrService: SsrService,
        @Inject(GET_FULL_DATE_STRING) private getFullDateString: GetFullDateStringProvider,
        @Inject(GET_DATE_STRING) private getDateString: GetDateStringProvider,
        @Inject(GET_SIMPLE_DATE_FROM_DATE_OBJECT) private getSimpleDateFromDateObject: GetSimpleDateFromDateObjectProvider,
    ) { }

    init(entityId: string, schedule: any, hasMealPeriod: boolean, date: Date = this.date) {
        this.entityId = entityId;
        this.hasMealPeriod = hasMealPeriod;
        const savedDate = this.storage.getDate();
        const getScheduleWithNewDate = this.fetchWithNewDate();
        if (savedDate || getScheduleWithNewDate) {
            // if there is a date stored by the user we will
            // need to call the services to get schedules for that day
            this.date = savedDate || getScheduleWithNewDate;
            this.getSchedules(savedDate).subscribe();
        } else {
            this.date = date;
        }
        this.fullFormatDate = this.getFullDateString(this.date, this.localeService.getLocale());
        this.schedule = schedule;
    }

    getSchedules(date: Date = this.date, entityId: string = this.entityId) {
        this.isLoading = true;
        this.entityId = entityId;
        this.date = date;
        this.storage.saveDate(date);
        this.fullFormatDate = this.getFullDateString(this.date, this.localeService.getLocale());
        this.finderApi.getSchedules(entityId, this.getDateString(date), this.hasMealPeriod)
            .subscribe((data: any) => {
                this.schedule = data.schedule;
                this.isLoading = false;

                // Send along meal period data if it exists
                if (this.hasMealPeriod) {
                    this.mealPeriodScheduleService.mealPeriodData = data;
                }

                this.subject.next(this.schedule);
            });

        return this.scheduleUpdated;
    }

    displayScheduleList(scheduleType) {
        // If it's closed or under refurbishment then we don't want to display the "-" or list of times
        return ![SCHEDULE_TYPES.refurbishment, SCHEDULE_TYPES.closedSchedule].includes(scheduleType);
    }

    getScheduleLabels(labels) {
        const scheduleLabels = {};

        Object.keys(SCHEDULE_TYPES).forEach((key) => {
            scheduleLabels[SCHEDULE_TYPES[key]] = labels[key];
        });

        return scheduleLabels;
    }

    private fetchWithNewDate(): Date {
        // @SSR: if the data comes from ssr it could be old. Request new schedule data.
        if (!this.ssrService.isDataFromTheServer()) {
            return void 0;
        }

        const todayDateObj = new Date();

        // Get the date the page was created
        const ssrCreated = this.ssrService.getCreatedDate();
        // Get todays date
        const todaysDate = this.getSimpleDateFromDateObject(todayDateObj, LOCALES.en_US_normalized);

        return ssrCreated !== todaysDate ? todayDateObj : void 0;
    }
}
